// eslint-disable-next-line no-unused-vars
import { Fragment, h, createRef } from 'preact';
import PropTypes from 'prop-types';
import { useCallback } from 'preact/hooks';
import Autocomplete from './Autocomplete';
import FacetExpandCollapse from './FacetExpandCollapse';
import Item from './Item';

const { __, sprintf } = wp.i18n;

/**
 * Autocomplete list component.
 * Used to show large sized list options.
 *
 * @param {object} props Props
 * @param {string} props.type The input type.
 * @param {Array} props.items The array of item objects.
 * @param {string} props.name The input name.
 * @param {string} props.label The facet label.
 * @returns {object} JSX
 */
const ListAutocomplete = ( { type, items, name, label } ) => {

	const ref = createRef();
	const checkedItems = items.filter( item => item.selected );
	const uncheckedItems = items.filter( item => !item.selected );

	/**
	 * Content component.
	 *
	 * @returns {object} JSX
	 */
	const Content = () => {
		return (
			<>
				{checkedItems.map( ( item ) => {
					return <Item item={item} key={item.id} group={name} type={type} />;
				} )}
				{uncheckedItems.map( ( item, index ) => {
					if ( 10 < index + 1 ) {
						return <Item item={item} key={item.id} group={name} type={type} hidden />;
					}

					return <Item item={item} key={item.id} group={name} type={type} />;
				} )
				}
			</>
		);
	};

	/**
	 * Expanded content component.
	 *
	 * @returns {object} JSX
	 */
	const ExpandedContent = useCallback( () => {
		/**
		 * Get item object by label.
		 *
		 * @param {string} label The label to look for.
		 * @returns {object|boolean}
		 */
		const getItemObjectByLabel = ( label ) => {
			const filtered = items.filter( ( item ) => label === item.label );

			if ( filtered.length ) {
				return filtered[0];
			}

			return false;
		};

		/**
		 * Handle on confirm.
		 * @param {string} value Item value.
		 * @returns
		 */
		const onConfirm = ( value ) => {
			const item = getItemObjectByLabel( value );

			if ( !item ) {
				return;
			}

			const uniqueId = `field-${name}-${item.id}-${item.value}`
				.toLowerCase()
				.replace( ' ', '_' );
			const input = ref.current.querySelector( `#${uniqueId}` );

			input.click();
		};

		/**
		 * Handle on suggest.
		 * @param {string} value Item value.
		 * @returns
		 */
		const onSuggest = ( value ) => {
			const item = getItemObjectByLabel( value );

			return `${item.label} (${item.count})`;
		};

		return (
			<Autocomplete
				id={`${name}-autocomplete`.toLowerCase().replace( ' ', '_' )}
				source={uncheckedItems.map( ( item ) => {
					return item.label;
				} )}
				displayMenu="overlay"
				minLength={2}
				onConfirm={onConfirm}
				templates={{
					suggestion: onSuggest,
				}}
				label={sprintf(
					/* translators: %s: the number of items, %s: the label of the facet */
					__( 'Search all %s %s', 'ms-research' ),
					items.length,
					label.toLowerCase()
				)}
			/>
		);
	}, [items, label, name, ref] );

	return (
		<div className="facet facet--autocomplete" ref={ref}>
			<FacetExpandCollapse
				mainContent={<Content />}
				expandedContent={<ExpandedContent />}
				buttonLabel={sprintf(
					/* translators: %s: the number of items, %s: the label of the facet */
					__( '+ Search all %s %s', 'ms-research' ),
					items.length,
					label.toLowerCase()
				)}
				group={name}
			/>
		</div>
	);
};

ListAutocomplete.propTypes = {
	type: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	name: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
};

export default ListAutocomplete;
